import { collection, getDocs, doc, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { ROLES } from '../hooks/useAuth';

// Firestore Timestamp'larını ve Date objelerini string'e çeviren yardımcı fonksiyon
const convertTimestampsToStrings = (obj) => {
  if (obj === null || obj === undefined) return obj;
  
  // Firestore Timestamp
  if (typeof obj.toDate === 'function') {
    return obj.toDate().toISOString();
  }
  
  // Normal Date objesi
  if (obj instanceof Date) {
    return obj.toISOString();
  }

  // Array'leri gez
  if (Array.isArray(obj)) {
    return obj.map(item => convertTimestampsToStrings(item));
  }
  
  // Objeleri gez
  if (typeof obj === 'object') {
    // Firestore'dan gelen doğrudan serialize edilmiş Timestamp nesnesi
    if ('seconds' in obj && 'nanoseconds' in obj && Object.keys(obj).length === 2) {
      return new Date(obj.seconds * 1000).toISOString();
    }
      
    const result = {};
    for (const key in obj) {
      result[key] = convertTimestampsToStrings(obj[key]);
    }
    return result;
  }
  
  // String, number, boolean döner
  return obj;
};

// ISO 8601 string kalıbını kontrol eden yardımcı fonksiyon
const isIsoDateString = (value) => {
  if (typeof value !== 'string') return false;
  // Sıkı bir ISO 8601 kontrolü
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;
  return isoRegex.test(value);
};

// String tarihlerini Date objelerine çeviren yardımcı fonksiyon
const convertStringsToDates = (obj) => {
  if (obj === null || obj === undefined) return obj;
  
  // Sadece ISO formatındaki tarih stringlerini Date yap
  if (isIsoDateString(obj)) {
    const date = new Date(obj);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertStringsToDates(item));
  }

  if (typeof obj === 'object') {
    const result = {};
    for (const key in obj) {
      result[key] = convertStringsToDates(obj[key]);
    }
    return result;
  }

  return obj;
};

// Batch write yardımcı fonksiyonu (Firestore 500 limit'ini aşmamak için)
const batchWrite = async (db, items, collectionName) => {
  const BATCH_SIZE = 499; // Güvenli limit
  let totalWritten = 0;

  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const chunk = items.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    for (const item of chunk) {
      const itemDataRaw = { ...item };
      const itemId = itemDataRaw.id;
      delete itemDataRaw.id;
      const itemData = convertStringsToDates(itemDataRaw);
      const docRef = doc(db, collectionName, itemId);
      batch.set(docRef, itemData);
    }

    await batch.commit();
    totalWritten += chunk.length;
    console.log(`${collectionName}: ${totalWritten}/${items.length} yazıldı`);
  }

  return totalWritten;
};

// Tüm verileri export et
export const exportData = async (userRole = null, userId = null) => {
  try {
    console.log('Backup başlatılıyor...', { userRole, userId });
    
    // 1. Müşterileri çek - Yazılımcı rolü için sadece kendi oluşturduğu müşteriler
    let customersQuery = collection(db, 'customers');
    if (userRole === ROLES.YAZILIMCI && userId) {
      customersQuery = query(collection(db, 'customers'), where('createdBy', '==', userId));
    }
    
    const customersSnapshot = await getDocs(customersQuery);
    const customers = customersSnapshot.docs.map(doc => {
      const data = convertTimestampsToStrings(doc.data());
      return { id: doc.id, ...data };
    });

    // 2. Siparişleri çek - Yazılımcı rolü için sadece kendi müşterilerine ait siparişler
    let orders = [];
    if (userRole === ROLES.YAZILIMCI && userId && customers.length > 0) {
      const customerIds = customers.map(c => c.id);
      if (customerIds.length > 0) {
        const allOrders = [];
        // Firestore 'in' query limit is 10 items (newer versions 30, but 10 is safe)
        for (let i = 0; i < customerIds.length; i += 10) {
          const batch = customerIds.slice(i, i + 10);
          const batchQuery = query(collection(db, 'orders'), where('customerId', 'in', batch));
          const batchSnapshot = await getDocs(batchQuery);
          const batchOrders = batchSnapshot.docs.map(doc => {
            const data = convertTimestampsToStrings(doc.data());
            return { id: doc.id, ...data };
          });
          allOrders.push(...batchOrders);
        }
        orders = allOrders;
      }
    } else {
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      orders = ordersSnapshot.docs.map(doc => {
        const data = convertTimestampsToStrings(doc.data());
        return { id: doc.id, ...data };
      });
    }

    // 3. Firmaları (Companies) çek - Bunlar siparişlerle alakalı olduğu için yedeklenmeli
    const companiesSnapshot = await getDocs(collection(db, 'companies'));
    const companies = companiesSnapshot.docs.map(doc => {
      const data = convertTimestampsToStrings(doc.data());
      return { id: doc.id, ...data };
    });

    // Toplam parça sayısını (adet) hesapla (Dashboard'daki mantıkla aynı)
    const totalPieces = orders.reduce((sum, order) => {
      if (order.productType === 'plise') {
        return sum + (order.pliseItems || []).reduce((pSum, item) => pSum + (parseInt(item.quantity) || 0), 0);
      }
      return sum + (parseInt(order.quantity) || 0);
    }, 0);

    // Backup objesi oluştur
    const backup = {
      exportDate: new Date().toISOString(),
      version: '2.1', // Parça sayısı eklendi
      totalCustomers: customers.length,
      totalOrders: orders.length,
      totalPieces: totalPieces,
      totalCompanies: companies.length,
      customers: customers,
      orders: orders,
      companies: companies
    };

    // JSON olarak indir
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ufuk-perde-full-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`Backup tamamlandı: ${customers.length} müşteri, ${orders.length} sipariş (${totalPieces} adet), ${companies.length} firma`);
    return { 
      success: true, 
      customers: customers.length, 
      orders: orders.length,
      pieces: totalPieces,
      companies: companies.length
    };
  } catch (error) {
    console.error('Backup hatası:', error);
    throw new Error('Backup oluşturulurken hata oluştu: ' + error.message);
  }
};

// JSON dosyasından verileri import et
export const importData = async (file) => {
  try {
    console.log('Import başlatılıyor...');
    
    // Dosyayı oku
    const text = await file.text();
    const backup = JSON.parse(text);

    // Backup formatını kontrol et
    if (!backup.customers || !backup.orders) {
      throw new Error('Geçersiz backup dosyası formatı');
    }

    // 1. Müşterileri batch olarak import et
    console.log(`${backup.customers.length} müşteri import ediliyor...`);
    const importedCustomers = await batchWrite(db, backup.customers, 'customers');

    // 2. Siparişleri batch olarak import et
    console.log(`${backup.orders.length} sipariş import ediliyor...`);
    const importedOrders = await batchWrite(db, backup.orders, 'orders');

    // 3. Firmaları (Companies) import et (eğer yedekte varsa)
    let importedCompanies = 0;
    if (backup.companies && backup.companies.length > 0) {
      console.log(`${backup.companies.length} firma import ediliyor...`);
      importedCompanies = await batchWrite(db, backup.companies, 'companies');
    }

    const result = {
      success: true,
      addedCustomers: importedCustomers,
      updatedCustomers: 0,
      addedOrders: importedOrders,
      updatedOrders: 0,
      addedCompanies: importedCompanies,
      totalCustomers: backup.totalCustomers,
      totalOrders: backup.totalOrders,
      totalCompanies: backup.totalCompanies || 0
    };

    console.log('Import tamamlandı:', result);
    return result;
  } catch (error) {
    console.error('Import hatası:', error);
    throw new Error('Import işlemi sırasında hata oluştu: ' + error.message);
  }
};

// Dosya formatını kontrol et
export const validateBackupFile = (file) => {
  // Dosya tipi kontrolü
  if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
    throw new Error('Sadece JSON dosyaları desteklenir');
  }

  // Dosya boyutu kontrolü (max 50MB)
  const maxSize = 50 * 1024 * 1024; // 50MB
  if (file.size > maxSize) {
    throw new Error('Dosya boyutu çok büyük (maksimum 50MB)');
  }

  return true;
};
